import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { connectionsApi } from '../api/endpoints'
import { ContactConsentDialog } from '../components/ContactConsentDialog'
import type { ConnectConsentResponse } from '../types/api'

/** One fetch per page load, shared by every screen; a failed fetch is dropped so the next ask retries. */
let cached: Promise<ConnectConsentResponse | null> | null = null

function loadConsent(): Promise<ConnectConsentResponse | null> {
  if (!cached) {
    cached = connectionsApi.consent().catch(() => {
      cached = null
      return null
    })
  }
  return cached
}

type Stage = 'initiate' | 'accept'

interface Pending {
  stage: Stage
  statement: string
}

/**
 * The agreement to share contact details before a connection is sent or accepted.
 *
 * `ask` resolves to:
 *  - `true`      - they agreed, send `contactConsent: true`;
 *  - `false`     - they cancelled, do nothing;
 *  - `undefined` - nothing to agree to (connections are not running through the network, or the
 *                  statements could not be loaded), send the request as it always was and let the
 *                  server decide - if it needed the agreement it says so in words.
 *
 * Render `dialog` somewhere in the screen; it is null unless a question is open.
 */
export function useContactConsent(): {
  ask: (stage: Stage, counterparty: string) => Promise<boolean | undefined>
  dialog: ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)
  // The open question's resolver, kept outside state so a later ask or an unmount can still reach it.
  const open = useRef<((agreed: boolean) => void) | null>(null)

  const close = useCallback((agreed: boolean) => {
    open.current?.(agreed)
    open.current = null
    setPending(null)
  }, [])

  // A page left with the dialog open is a cancel, not a promise that never comes back.
  useEffect(() => () => close(false), [close])

  const ask = useCallback(
    async (stage: Stage, counterparty: string) => {
      const consent = await loadConsent()
      if (!consent || !consent.viaNetwork) return undefined
      const statement = consent[stage].text.replace(/__COUNTERPARTY__/g, counterparty)
      // A second question while one is open replaces it; the first is answered as cancelled.
      close(false)
      return new Promise<boolean>((resolve) => {
        open.current = resolve
        setPending({ stage, statement })
      })
    },
    [close],
  )

  const settle = close

  const dialog = pending ? (
    <ContactConsentDialog
      title={pending.stage === 'initiate' ? 'Share your contact details?' : 'Accept and share your contact details?'}
      statement={pending.statement}
      confirmLabel={pending.stage === 'initiate' ? 'Send request' : 'Accept'}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null

  return { ask, dialog }
}
